import {
  AlertTriangle,
  Shield,
  Activity,
  Network,
  Clock,
  Link as LinkIcon,
  ChevronDown,
  Loader2,
  Server,
} from "lucide-react";
import { useGraphStore } from "../store/graphStore";
import { Button } from "../../../ui/common-ui/button";
import { Badge } from "../../../ui/common-ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/common-ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/common-ui/collapsible";


import { useState, useEffect } from "react";
import { AlertInspector } from "./AlertInspector";
import { UserInspector } from "./UserInspector";
import { ServiceAccountInspector } from "./ServiceAccountInspector";
import { ResourceInspector } from "./ResourceInspector";
import { CorrelationInspector } from "./CorrelationInspector";
import { BlastRadiusInspector } from "./BlastRadiusInspector";
import { EntityIdentityHeader } from "./EntityIdentityHeader";
import { ResourceGroupInspector } from "./ResourceGroupInspector";
import { PolicyInspector } from "./PolicyInspector";
import {
  fetchUserAnalyticsResolutions,
  fetchUserAnalyticsSuggestions,
  fetchRawLogs,
  ResolutionsResponse,
  SuggestionsResponse,
  RawLogEntry,
} from "../services/cloudApi";
import type { CloudType } from "../types/api";
import type { GraphNode } from "../types/graph";

// Helper to get the correct entity parameter for activity/analytics APIs based on cloud type
const getActivityEntityParam = (node: GraphNode): string => {
  const props = node.props || {};
  const cloudType = props.cloudType;

  if (cloudType === 'AWS') {
    // For AWS users, send arn. For AWS IAM roles (service accounts), send roleArn
    if (node.type === 'user') {
      return props.arn || '';
    } else if (node.type === 'service_account') {
      return props.roleArn || '';
    } else if (node.type === 'resource') {
      return props.resourceArn || props.arn || props.resourceId || '';
    }
  } else if (cloudType === 'AZURE') {
    // Azure analytics expects userId for users and principalId for managed identities.
    if (node.type === 'user') {
      return props.userId || props.azureId || props.id || '';
    } else if (node.type === 'service_account') {
      return props.principalId || '';
    } else if (node.type === 'resource') {
      return props.resourceId || props.id || '';
    }
  }

  // Default for GCP - use email
  return props.email || props.serviceAccountEmail || props.name || props.username || '';
};

// Helper to get the correct cloudId parameter based on cloud type
const getCloudIdParam = (node: GraphNode): string => {
  const props = node.props || {};

  // Prefer the normalized cloudId we stamp on every node in graphApi.transformApiDataToGraph
  if (props.cloudId) return props.cloudId;

  const cloudType = props.cloudType;

  if (cloudType === 'AWS') {
    return props.accountId || '';
  } else if (cloudType === 'AZURE') {
    return props.azureSubscriptionId || props.subscriptionId || '';
  }
  // Default for GCP
  return props.projectId || '';
};

// Resolutions Component - Organized by Role with API data
interface ResolutionsSectionProps {
  entity: string;
  tenantName: string;
  cloudId: string;
  cloudType: CloudType;
  roleNames?: Record<string, string>;
}

function ResolutionsSection({ entity, tenantName, cloudId, cloudType, roleNames = {} }: ResolutionsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [data, setData] = useState<ResolutionsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!entity || !tenantName || !cloudId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await fetchUserAnalyticsResolutions(tenantName, cloudId, entity, cloudType);
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, [entity, tenantName, cloudId, cloudType]);

  const toggleRole = (role: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(role)) {
      newExpanded.delete(role);
    } else {
      newExpanded.add(role);
    }
    setExpandedRoles(newExpanded);
  };

  const getRoleName = (roleId: string) => {
    if (roleNames[roleId]) return roleNames[roleId];
    return roleId.split('/').pop() || roleId;
  };

  // Transform API data into displayable format
  const roleResolutions = data?.resolutions
    ? Object.entries(data.resolutions).map(([roleId, resourceMap]) => {
      const projectRoles = Array.isArray(resourceMap.roles)
        ? resourceMap.roles.map(String)
        : [];
      return {
        roleId,
        roleName: getRoleName(roleId),
        entries: projectRoles.length > 0
          ? [{ resourceName: "Project level", recommendedRoles: projectRoles }]
          : Object.entries(resourceMap).map(([resourceName, recommendedRoles]) => ({
              resourceName,
              recommendedRoles: Array.isArray(recommendedRoles)
                ? recommendedRoles.map(String)
                : [String(recommendedRoles)],
            })),
      };
    })
    : [];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
        <CardHeader className="pb-3 px-0 pt-0">
          <CollapsibleTrigger className="flex items-center justify-between w-full hover:bg-muted/50 p-4 transition-colors rounded-t-xl">
            <div className="text-left">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-blue-500/10">
                  <Shield className="w-4 h-4 text-blue-500" />
                </div>
                Resolve Project-Level Roles
              </CardTitle>
              <CardDescription className="text-xs mt-1.5">
                Recommended role replacements for least-privilege access
              </CardDescription>
            </div>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : roleResolutions.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No resolutions available</p>
              </div>
            ) : (
              roleResolutions.map((roleData) => {
                const isExpanded = expandedRoles.has(roleData.roleId);
                return (
                  <div
                    key={roleData.roleId}
                    className="border border-border/50 rounded-xl overflow-hidden bg-card"
                  >
                    <button
                      onClick={() => toggleRole(roleData.roleId)}
                      className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                        <span className="text-sm font-semibold text-foreground truncate">
                          {roleData.roleName}
                        </span>
                        <Badge variant="secondary" className="text-[10px] shrink-0">
                          {roleData.entries.length}
                        </Badge>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 shrink-0 ml-2 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="border-t border-border/50 bg-muted/20 p-3 space-y-2">
                        {/* Top-down list of resource -> recommended roles */}
                        <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-hidden">
                          {roleData.entries.map((entry, idx) => (
                            <div
                              key={idx}
                              className="p-2.5 bg-background rounded-lg border border-border/40 text-xs"
                            >
                              <div className="flex items-start gap-2">
                                <Server className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-foreground truncate">{entry.resourceName}</p>
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {entry.recommendedRoles.map((rec, ridx) => (
                                      <span
                                        key={ridx}
                                        className="text-[10px] font-mono bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded border border-green-500/20"
                                      >
                                        {getRoleName(rec)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Single Resolve button at the bottom */}
                        <Button
                          size="sm"
                          className="w-full h-8 text-xs font-semibold bg-blue-500 hover:bg-blue-600 text-white mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log("Resolve role:", roleData.roleId, roleData.entries);
                          }}
                        >
                          Resolve
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Suggestions Component - With API data
interface SuggestionsSectionProps {
  entity: string;
  tenantName: string;
  cloudId: string;
  cloudType: CloudType;
  roleNames?: Record<string, string>;
}

function SuggestionsSection({ entity, tenantName, cloudId, cloudType, roleNames = {} }: SuggestionsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<SuggestionsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!entity || !tenantName || !cloudId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await fetchUserAnalyticsSuggestions(tenantName, cloudId, entity, cloudType);
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, [entity, tenantName, cloudId, cloudType]);

  const getRoleName = (roleId: unknown): string => {
    if (typeof roleId !== 'string') {
      return String(roleId);
    }
    if (roleNames[roleId]) return roleNames[roleId];
    return roleId.split('/').pop() || roleId;
  };

  // Transform API data: key is resource name (or "project-level"), value is role ID
  const suggestions = data?.suggestions
    ? Object.entries(data.suggestions)
      .filter(([_, roleId]) => roleId != null)
      .map(([resourceKey, roleId]) => ({
        resourceKey,
        roleId: typeof roleId === 'string' ? roleId : String(roleId),
        roleName: getRoleName(roleId),
        isProjectLevel: resourceKey === 'project-level',
      }))
    : [];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
        <CardHeader className="pb-3 px-0 pt-0">
          <CollapsibleTrigger className="flex items-center justify-between w-full hover:bg-muted/50 p-4 transition-colors rounded-t-xl">
            <div className="text-left">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-destructive/10">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                </div>
                Suggested Role Cleanup
              </CardTitle>
              <CardDescription className="text-xs mt-1.5">
                Over-privileged roles that can be safely removed
              </CardDescription>
            </div>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-2.5">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No suggestions available</p>
              </div>
            ) : (
              suggestions.map((item, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-xl border border-border/50 bg-card space-y-2"
                >
                  {/* Show resource only if NOT project-level */}
                  {!item.isProjectLevel && (
                    <div className="flex items-center gap-2 text-xs">
                      <Server className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Resource:</span>
                      <span className="font-medium text-foreground">{item.resourceKey}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Shield className="w-3.5 h-3.5 text-destructive shrink-0" />
                      <span className="text-sm font-semibold text-foreground truncate">
                        {item.roleName}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 text-[10px] font-semibold shrink-0"
                      onClick={() => console.log("Detach:", item)}
                    >
                      Detach
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Assigned Roles Section - Collapsible dropdown
interface AssignedRolesSectionProps {
  roles: string[];
  roleNames: Record<string, string>;
  entityLabel: string;
  entityType: string;
}

function AssignedRolesSection({ roles, roleNames, entityLabel, entityType }: AssignedRolesSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const getRoleName = (roleId: string) => {
    if (roleNames[roleId]) return roleNames[roleId];
    return roleId.split('/').pop() || roleId;
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
        <CardHeader className="pb-3 px-0 pt-0">
          <CollapsibleTrigger className="flex items-center justify-between w-full hover:bg-muted/50 p-4 transition-colors rounded-t-xl">
            <div className="text-left">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                Assigned Roles
                {roles.length > 0 && (
                  <Badge variant="secondary" className="text-[10px] ml-1">
                    {roles.length}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs mt-1.5">
                Roles assigned to this {entityType}
              </CardDescription>
            </div>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 px-4 pb-4">
            {roles.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-hidden">
                {roles.map((roleId: string, idx: number) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg border border-border/50 bg-card space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-2 h-2 rounded-full bg-primary shrink-0" />
                        <span className="text-sm font-semibold text-foreground truncate">
                          {getRoleName(roleId)}
                        </span>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-7 text-[10px] font-semibold shrink-0"
                        onClick={() => console.log("Detaching role:", roleId, "from", entityLabel)}
                      >
                        Detach
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No roles assigned</p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Activity Section - With Raw Logs API data
interface ActivitySectionProps {
  email: string;
  tenantName: string;
  cloudId: string;
  cloudType: CloudType;
}

function ActivitySection({ email, tenantName, cloudId, cloudType }: ActivitySectionProps) {
  const [logs, setLogs] = useState<RawLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!email || !tenantName || !cloudId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await fetchRawLogs(tenantName, cloudId, email, cloudType);
      setLogs(result);
      setLoading(false);
    };
    fetchData();
  }, [email, tenantName, cloudId, cloudType]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const getLogTypeVariant = (logType: string) => {
    switch (logType?.toUpperCase()) {
      case 'ERROR':
      case 'HIGH':
        return 'destructive';
      case 'WARNING':
      case 'WARN':
        return 'default';
      default:
        return 'secondary';
    }
  };

  // Get status-based styling for the activity card
  const getStatusStyles = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s === 'success' || s === 'succeeded' || s === 'ok' || s === '200' || s === 'completed') {
      return {
        border: 'border-green-500/40',
        bg: 'from-green-500/10 to-green-500/5',
        statusBadge: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
      };
    }
    if (s === 'failed' || s === 'failure' || s === 'error' || s === 'denied' || s === '403' || s === '401' || s === '500') {
      return {
        border: 'border-red-500/40',
        bg: 'from-red-500/10 to-red-500/5',
        statusBadge: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30',
      };
    }
    if (s === 'warning' || s === 'partial' || s === 'timeout') {
      return {
        border: 'border-yellow-500/40',
        bg: 'from-yellow-500/10 to-yellow-500/5',
        statusBadge: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30',
      };
    }
    // Default neutral styling
    return {
      border: 'border-border/40',
      bg: 'from-muted/30 to-muted/10',
      statusBadge: 'bg-muted text-muted-foreground border-border/50',
    };
  };

  return (
    <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-500/10">
            <Activity className="w-4 h-4 text-blue-500" />
          </div>
          Recent Cloud Activities
        </CardTitle>
        <CardDescription className="text-xs">
          Latest activity logs for this entity
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No activity logs available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.slice(0, 20).map((log, idx) => {
              const statusStyles = getStatusStyles(log.status || '');
              return (
                <div
                  key={log.id || idx}
                  className={`relative rounded-xl border ${statusStyles.border} bg-gradient-to-br ${statusStyles.bg} overflow-hidden`}
                >
                  {/* Header with badge and timestamp */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 border-b border-border/30">
                    <Badge
                      variant={getLogTypeVariant(log.logType)}
                      className="text-[10px] font-bold px-2.5 py-0.5 rounded-full"
                    >
                      {log.logType || 'INFO'}
                    </Badge>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                      <Clock className="h-3 w-3" />
                      <span>{formatTimestamp(log.logTimestamp)}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    {/* Action */}
                    <div className="font-mono text-xs text-foreground bg-background/60 px-3 py-2 rounded-lg border border-border/20">
                      {log.action}
                    </div>

                    {/* Resource (entityAssociated) */}
                    {log.entityAssociated && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Resource</span>
                        <p className="text-xs text-foreground break-all leading-relaxed">{log.entityAssociated}</p>
                      </div>
                    )}

                    {/* Source IP */}
                    {log.sourceIp && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <Network className="w-3 h-3 text-muted-foreground" />
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Source IP</span>
                        </div>
                        <p className="text-xs text-foreground font-mono break-all leading-relaxed">{log.sourceIp}</p>
                      </div>
                    )}

                    {/* Status with color */}
                    {log.status && (
                      <div className="flex items-center gap-2 pt-1">
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Status</span>
                        <Badge variant="outline" className={`text-[10px] font-medium ${statusStyles.statusBadge}`}>
                          {log.status}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Anomalies Section - Display connected anomalies for user/SA/resource nodes
interface AnomaliesSectionProps {
  nodeId: string;
  alertCount: number;
  alertIds?: string[]; // For resource nodes - specific alert IDs
}

function AnomaliesSection({ nodeId, alertCount, alertIds }: AnomaliesSectionProps) {
  const { allNodes, allEdges } = useGraphStore();

  // Find connected anomaly nodes for this entity
  const connectedAnomalies = allEdges
    .filter(edge => edge.source === nodeId && edge.type === 'emits_anomaly')
    .map(edge => allNodes.find(n => n.id === edge.target))
    .filter((node): node is GraphNode => node !== undefined && node !== null && node.type === 'anomaly');

  // Also check edges where anomaly is connected via props.graphId
  const graphId = nodeId.startsWith('user-') ? nodeId : nodeId.startsWith('sa-') ? nodeId : null;
  const graphAnomalies = graphId
    ? allNodes.filter(n => n.type === 'anomaly' && n.props?.graphId === graphId)
    : [];

  // For resources, find anomalies where alertId matches the resource's alertIds
  const resourceAnomalies = alertIds?.length
    ? allNodes.filter(n => n.type === 'anomaly' && alertIds.includes(n.props?.alertId))
    : [];

  // Combine and deduplicate
  const anomalies = [...new Map([...connectedAnomalies, ...graphAnomalies, ...resourceAnomalies].map(a => [a.id, a])).values()];

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/30 hover:border-red-500/50',
          icon: 'bg-red-500/20 text-red-500',
          badge: 'bg-red-500/20 text-red-500 border-red-500/30',
        };
      case 'high':
        return {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/30 hover:border-orange-500/50',
          icon: 'bg-orange-500/20 text-orange-500',
          badge: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
        };
      case 'medium':
        return {
          bg: 'bg-yellow-500/10',
          border: 'border-yellow-500/30 hover:border-yellow-500/50',
          icon: 'bg-yellow-500/20 text-yellow-500',
          badge: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
        };
      default:
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30 hover:border-blue-500/50',
          icon: 'bg-blue-500/20 text-blue-500',
          badge: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
        };
    }
  };

  const displayCount = anomalies.length || alertCount;

  return (
    <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
      <CardHeader className="pb-3 px-4 pt-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-destructive/10">
            <AlertTriangle className="w-4 h-4 text-destructive" />
          </div>
          Detected Alerts
          {displayCount > 0 && (
            <Badge variant="destructive" className="text-[10px] ml-1">
              {displayCount}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs mt-1.5">
          Security alerts detected for this entity
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 px-4 pb-4">
        {anomalies.length > 0 ? (
          <div className="space-y-3">
            {anomalies.map((anomaly, idx) => {
              const severity = anomaly.props?.severity || 'medium';
              const styles = getSeverityStyles(severity);
              const attackSteps = Object.keys(anomaly.props?.attackPath || {}).length;

              return (
                <div
                  key={anomaly.id || idx}
                  className={`p-4 rounded-xl border transition-all duration-200 ${styles.bg} ${styles.border}`}
                >
                  {/* Header with severity and time */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`p-1.5 rounded-lg shrink-0 ${styles.icon}`}>
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-foreground leading-tight">
                          {anomaly.label || 'Unknown Anomaly'}
                        </p>
                        {anomaly.props?.alertType && (
                          <Badge variant="outline" className="text-[9px] mt-1.5 px-1.5 py-0">
                            {anomaly.props.alertType}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] font-semibold shrink-0 ${styles.badge}`}>
                      {severity}
                    </Badge>
                  </div>

                  {/* Attack steps indicator */}
                  {attackSteps > 0 && (
                    <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                      <Activity className="w-3.5 h-3.5" />
                      <span>{attackSteps} attack step{attackSteps !== 1 ? 's' : ''} detected</span>
                    </div>
                  )}

                  {/* Info section - What happened */}
                  {anomaly.props?.info?.what && (
                    <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
                      {anomaly.props.info.what}
                    </p>
                  )}

                  {/* Affected resources */}
                  {anomaly.props && anomaly.props.affectedResources?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {anomaly.props.affectedResources.slice(0, 3).map((res: string, ridx: number) => (
                        <Badge key={ridx} variant="outline" className="text-[9px] font-mono px-1.5 py-0 bg-background/50">
                          {res}
                        </Badge>
                      ))}
                      {anomaly.props.affectedResources.length > 3 && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-background/50">
                          +{anomaly.props.affectedResources.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Footer with timestamp */}
                  <div className="flex items-center gap-2 pt-2 border-t border-border/20">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {formatTimestamp(anomaly.props?.createdAt || anomaly.props?.timeRangeEnd)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No alerts detected</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Inspector() {
  const { nodes, edges, selectedNodes, selectedEdges, inspectorOpen, activeMode } = useGraphStore();
  const [, setActiveTab] = useState<string>("summary");

  const selectedNode = selectedNodes.size > 0 ? nodes.find((n) => n.id === Array.from(selectedNodes)[0]) : null;
  const selectedEdge = selectedEdges.size > 0 ? edges.find((e) => e.id === Array.from(selectedEdges)[0]) : null;
  const hasSelection = selectedNode || selectedEdge;

  useEffect(() => {
    setActiveTab("summary");
  }, [selectedNode?.id, selectedEdge?.id]);

  if (!inspectorOpen) return null;

  return (
    <div
      className="w-80 xl:w-96 max-h-[calc(100dvh-12rem)] rounded-3xl shadow-xl nowheel"
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="bg-card max-h-[calc(100dvh-12rem)] rounded-3xl border border-border/50 flex flex-col overflow-hidden transition-all duration-500 ease-in-out">
        {/* Header */}
        <div className="p-5 flex-shrink-0 bg-card space-y-3">
          <div className="flex items-center justify-center">
            <h2 className="text-lg font-semibold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Inspector
            </h2>
          </div>
        </div>

        {/* Content with sticky tabs */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col transition-all duration-500 ease-in-out">
          {!hasSelection && (
            <div className="p-8 flex flex-col items-center justify-center text-center animate-fade-in">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-5 shadow-lg">
                <Network className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Selection</h3>
              <p className="text-sm text-muted-foreground max-w-[250px]">
                Click on a node in the graph to view its details and connections.
              </p>
            </div>
          )}
          {selectedNode && (
            <div className="animate-fade-in flex-1 min-h-0 flex flex-col overflow-hidden" key={selectedNode.id}>
              {/* All modes use same tabbed view for user/sa nodes */}
              {(() => {
                // Determine if we should show correlation/blast summary section
                const showCorrelationSummary = activeMode === 'correlations' && selectedNode.props?.isCorrelationMode;
                const showBlastSummary = activeMode === 'blast-radius' && selectedNode.props?.isBlastRadiusMode;
                const isEntityNode = selectedNode.type === 'user' || selectedNode.type === 'service_account';

                // For non-entity nodes in correlation/blast mode (secret, token, resource), show simplified view
                if ((activeMode === 'correlations' || activeMode === 'blast-radius') && !isEntityNode) {
                  return (
                    <div
                      className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hidden rounded-b-3xl nowheel p-5 space-y-4"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {activeMode === 'correlations' ? (
                        <CorrelationInspector node={selectedNode} />
                      ) : (
                        <BlastRadiusInspector node={selectedNode} />
                      )}
                    </div>
                  );
                }

                return (
                  <Tabs defaultValue="summary" className="w-full flex-1 min-h-0 flex flex-col overflow-hidden">
                    {/* Sticky Tabs Header */}
                    <TabsList className="w-full justify-center rounded-none flex-shrink-0 sticky top-0 z-10 bg-card shadow-sm">
                      <TabsTrigger value="summary" className="data-[state=active]:shadow-sm">
                        Summary
                      </TabsTrigger>
                      {(selectedNode.type === "user" || selectedNode.type === "service_account") && (
                        <>
                          <TabsTrigger value="privileges" className="data-[state=active]:shadow-sm">
                            Privileges
                          </TabsTrigger>
                          <TabsTrigger value="activity" className="data-[state=active]:shadow-sm">
                            Activity
                          </TabsTrigger>
                          <TabsTrigger value="anomalies" className="data-[state=active]:shadow-sm">
                            Alerts
                          </TabsTrigger>
                        </>
                      )}
                      {selectedNode.type === "resource" && (
                        <>
                          <TabsTrigger value="anomalies" className="data-[state=active]:shadow-sm">
                            Alerts
                          </TabsTrigger>
                        </>
                      )}
                    </TabsList>

                    {/* Scrollable Tab Content */}
                    <div
                      className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hidden rounded-b-3xl nowheel"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <TabsContent value="summary" className="p-5 space-y-4 m-0">
                        {/* Show Correlation/Blast summary at top when in those modes */}
                        {showCorrelationSummary && (
                          <CorrelationInspector node={selectedNode} />
                        )}
                        {showBlastSummary && (
                          <>
                            <EntityIdentityHeader node={selectedNode} />
                            <BlastRadiusInspector node={selectedNode} />
                          </>
                        )}

                        {/* Standard summary content */}
                        {selectedNode.type === "anomaly" ? (
                          <AlertInspector node={selectedNode} />
                        ) : selectedNode.type === "policy" ? (
                          <PolicyInspector node={selectedNode} />
                        ) : selectedNode.type === "user" ? (
                          <UserInspector node={selectedNode} hideIdentityHeader={showBlastSummary} />
                        ) : selectedNode.type === "service_account" ? (
                          <ServiceAccountInspector node={selectedNode} hideIdentityHeader={showBlastSummary} />
                        ) : selectedNode.type === "resource" ? (
                          <ResourceInspector node={selectedNode} />
                        ) : selectedNode.type === "resource_group" ? (
                          <ResourceGroupInspector node={selectedNode} />
                        ) : (
                          <>
                            <Card className="border-border/50 shadow-lg hover:shadow-xl transition-shadow duration-300">
                              <CardHeader className="pb-4"></CardHeader>
                              <CardContent className="space-y-5">

                                <div className="space-y-4">

                                  {/* For Role nodes - show role-specific details */}
                                  {selectedNode.type === "role" && selectedNode.props && (
                                    <>
                                      {selectedNode.props.fullRoleName && (
                                        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/40 hover:bg-muted/50 transition-colors duration-200">
                                          <div className="p-1.5 rounded-lg bg-primary/10">
                                            <Shield className="w-4 h-4 text-primary" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-muted-foreground mb-1.5">Full Role Name</p>
                                            <p className="text-xs font-mono bg-background/80 px-2.5 py-1.5 rounded-lg border border-border/30 break-all shadow-sm">
                                              {selectedNode.props.fullRoleName}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {selectedNode.props.connections !== undefined && (
                                        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/40 hover:bg-muted/50 transition-colors duration-200">
                                          <div className="p-1.5 rounded-lg bg-primary/10">
                                            <LinkIcon className="w-4 h-4 text-primary" />
                                          </div>
                                          <div className="flex-1">
                                            <p className="text-xs font-medium text-muted-foreground mb-1.5">Connections</p>
                                            <p className="text-sm font-semibold">
                                              {selectedNode.props.connections} active connection
                                              {selectedNode.props.connections !== 1 ? "s" : ""}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/40 hover:bg-muted/50 transition-colors duration-200">
                                        <div className="p-1.5 rounded-lg bg-primary/10">
                                          <Network className="w-4 h-4 text-primary" />
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-xs font-medium text-muted-foreground mb-1.5">Role ID</p>
                                          <p className="text-xs font-mono bg-background/80 px-2.5 py-1.5 rounded-lg border border-border/30 break-all shadow-sm">
                                            {selectedNode.id}
                                          </p>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="border-border/50 shadow-lg hover:shadow-xl transition-shadow duration-300">
                              <CardHeader>
                                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                  <LinkIcon className="w-4 h-4 text-primary" />
                                  Connected Entities
                                </CardTitle>
                                <CardDescription>Direct connections from this node</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {edges
                                    .filter((e) => e.source === selectedNode.id || e.target === selectedNode.id)
                                    .slice(0, 5)
                                    .map((edge) => {
                                      const connectedNodeId = edge.source === selectedNode.id ? edge.target : edge.source;
                                      const connectedNode = nodes.find((n) => n.id === connectedNodeId);

                                      return (
                                        <div
                                          key={edge.id}
                                          className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/40 hover:bg-muted/50 hover:shadow-sm transition-all duration-200"
                                        >
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold truncate">
                                              {connectedNode?.label || connectedNodeId}
                                            </p>
                                            <p className="text-xs text-muted-foreground capitalize mt-0.5">
                                              {edge.type.replace("_", " ")} {edge.count ? `(${edge.count} events)` : ""}
                                            </p>
                                          </div>
                                          <Badge variant="outline" className="text-xs capitalize ml-2 shadow-sm">
                                            {connectedNode?.type?.replace("_", " ") || 'Unknown'}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  {edges.filter((e) => e.source === selectedNode.id || e.target === selectedNode.id).length >
                                    5 && (
                                      <p className="text-xs text-muted-foreground text-center pt-2 font-medium">
                                        And{" "}
                                        {edges.filter((e) => e.source === selectedNode.id || e.target === selectedNode.id).length -
                                          5}{" "}
                                        more connections...
                                      </p>
                                    )}
                                </div>
                              </CardContent>
                            </Card>
                          </>
                        )}
                      </TabsContent>

                      <TabsContent value="privileges" className="p-5 space-y-4 m-0">
                        <AssignedRolesSection
                          roles={selectedNode.props?.roles || []}
                          roleNames={selectedNode.props?.roleNames || {}}
                          entityLabel={selectedNode.label}
                          entityType={selectedNode.type === "user" ? "user" : "service account"}
                        />

                        <ResolutionsSection
                          entity={getActivityEntityParam(selectedNode)}
                          tenantName={selectedNode.props?.tenantName || ''}
                          cloudId={getCloudIdParam(selectedNode)}
                          cloudType={(selectedNode.props?.cloudType as CloudType) || 'GCP'}
                          roleNames={selectedNode.props?.roleNames || {}}
                        />
                        <SuggestionsSection
                          entity={getActivityEntityParam(selectedNode)}
                          tenantName={selectedNode.props?.tenantName || ''}
                          cloudId={getCloudIdParam(selectedNode)}
                          cloudType={(selectedNode.props?.cloudType as CloudType) || 'GCP'}
                          roleNames={selectedNode.props?.roleNames || {}}
                        />
                      </TabsContent>

                      <TabsContent value="permissions" className="p-5 space-y-4 m-0">
                        <Card className="border-border/50 shadow-lg hover:shadow-xl transition-shadow duration-300">
                          <CardHeader>
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <div className="p-1.5 rounded-lg bg-primary/10">
                                <Shield className="w-4 h-4 text-primary" />
                              </div>
                              Permissions Used
                            </CardTitle>
                            <CardDescription>Permissions assigned to this resource</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {[
                                "compute.instances.get",
                                "compute.instances.list",
                                "compute.instances.start",
                                "compute.instances.stop",
                                "storage.buckets.get",
                                "storage.objects.list",
                                "iam.serviceAccounts.get",
                              ].map((permission, idx) => (
                                <div
                                  key={idx}
                                  className="p-3 rounded-lg border border-border/50 bg-card hover:bg-accent/30 transition-colors duration-200"
                                >
                                  <span className="text-sm font-mono font-medium">{permission}</span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>

                      <TabsContent value="activity" className="p-5 space-y-4 m-0">
                        <ActivitySection
                          email={getActivityEntityParam(selectedNode)}
                          tenantName={selectedNode.props?.tenantName || ''}
                          cloudId={getCloudIdParam(selectedNode)}
                          cloudType={(selectedNode.props?.cloudType as CloudType) || 'GCP'}
                        />
                      </TabsContent>

                      <TabsContent value="anomalies" className="p-5 space-y-4 m-0">
                        <AnomaliesSection
                          nodeId={selectedNode.id}
                          alertCount={selectedNode.props?.alertCount || 0}
                          alertIds={selectedNode.props?.alertIds}
                        />
                      </TabsContent>
                    </div>
                  </Tabs>
                );
              })()}
            </div>
          )}
          {selectedEdge && (
            <div className="p-5 space-y-4 animate-fade-in">
              <Card className="border-border/50 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <LinkIcon className="w-4 h-4 text-primary" />
                    </div>
                    Connection Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Source</span>
                    <span className="text-sm font-mono font-semibold truncate ml-2">{selectedEdge.source}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Target</span>
                    <span className="text-sm font-mono font-semibold truncate ml-2">{selectedEdge.target}</span>
                  </div>
                  {selectedEdge.count && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/40">
                      <span className="text-xs font-medium text-muted-foreground">Event Count</span>
                      <Badge className="shadow-sm">{selectedEdge.count}</Badge>
                    </div>
                  )}
                  {selectedEdge.last_seen && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/40">
                      <span className="text-xs font-medium text-muted-foreground">Last Seen</span>
                      <span className="text-sm font-semibold">{new Date(selectedEdge.last_seen).toLocaleString()}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
